import { Head, Link } from "@inertiajs/react"
import { ChevronRight, Plus } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { ChannelIcon } from "@/lib/channel-icons"
import { SettingsSubNav } from "../SettingsSubNav"

type ChannelSummary = {
  slug: string
  name: string
  channel_type: string
  color: string
  character_limit: number | null
  slot_count: number
  piece_count: number
}

export default function ChannelsIndex({ channels }: { channels: ChannelSummary[] }) {
  return (
    <>
      <Head title="Channels">
        <meta name="description" content="Configure SparkDrop's publishing channels." />
        <meta property="og:title" content="Channels" />
        <meta property="og:description" content="Configure SparkDrop's publishing channels." />
      </Head>
      <AppShell>
        <PageHeader
          title="Settings"
          description="Configure how SparkDrop talks to the world."
          tabs={<SettingsSubNav active="channels" />}
        />

        <section className="mt-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold">Channels</h2>
              <p className="mt-1 text-sm text-ink-muted">
                Every destination SparkDrop can publish to, with its capability
                flags and slot templates.
              </p>
            </div>
            <Link href="/settings/channels/new">
              <Button variant="secondary" size="sm">
                <Plus className="h-3 w-3" /> New channel
              </Button>
            </Link>
          </div>

          {channels.length === 0 ? (
            <p className="mt-6 text-sm text-ink-muted">No channels yet.</p>
          ) : (
            <ul className="mt-6 divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
              {channels.map((c) => (
                <li key={c.slug}>
                  <Link
                    href={`/settings/channels/${c.slug}`}
                    className="flex items-center gap-3 px-4 py-3 no-underline hover:bg-surface"
                  >
                    <ChannelIcon
                      channelType={c.channel_type}
                      color={c.color}
                      className="h-5 w-5"
                    />
                    <div className="min-w-0 flex-1 flex items-center gap-3 text-sm">
                      <span className="font-medium text-ink-display">{c.name}</span>
                      <span className="text-xs text-ink-muted">
                        {c.slot_count} slot{c.slot_count === 1 ? "" : "s"} / {c.piece_count} piece{c.piece_count === 1 ? "" : "s"}
                      </span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-ink-muted" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </AppShell>
    </>
  )
}
